import { logger } from "../services/logger.ts";
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowDownRight, 
  ArrowUpRight, 
  X, 
  Wallet,
  DollarSign,
  TrendingUp,
  Inbox,
  CreditCard,
  TrendingDown,
  Repeat,
  Gift,
  Briefcase,
  AlertCircle
} from 'lucide-react';
import { useAuth } from "../lib/auth.tsx";
import { supabase } from "../lib/db.ts";
import { useSettings } from "../lib/settings/settingsContext.tsx";
import { WalletTransaction } from "../types.ts";
import { TransactionHistory } from "../components/portfolio/TransactionHistory.tsx";
import { PeachPaymentForm } from "../components/PeachPaymentForm.tsx";
import { checkPeachPaymentStatus } from "../lib/api.ts";
import { usePromoConfig, areDepositsDisabled, getDepositDisabledMessage } from "../hooks/usePromoConfig.ts";
import { FICADocumentModal } from "../components/FICADocumentModal.tsx";

type Transaction = WalletTransaction;

interface WalletData {
  balance: number;
}

export function WalletManagement() {
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showFICAModal, setShowFICAModal] = useState(false);
  const [showVirtualCurrencyWarning, setShowVirtualCurrencyWarning] = useState(false);
  const [pendingAction, setPendingAction] = useState<'withdraw' | 'fica' | null>(null);
  const [ficaVerified, setFicaVerified] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const TRANSACTIONS_PER_PAGE = 50;
  const { user } = useAuth();
  const { formatCurrency } = useSettings();
  const { promoConfig } = usePromoConfig();
  const observer = useRef<IntersectionObserver | null>(null);
  
  // Listen for payment success events
  useEffect(() => {
    const handlePaymentSuccess = (event: CustomEvent) => {
      if (event.detail && event.detail.message) {
        setSuccessMessage(event.detail.message);
      }
    };

    // Add event listener
    window.addEventListener('paymentSuccess', handlePaymentSuccess as EventListener);

    // Remove event listener on cleanup
    return () => {
      window.removeEventListener('paymentSuccess', handlePaymentSuccess as EventListener);
    };
  }, []);
  
  // Reference for the load more trigger element
  const loadMoreTriggerRef = useCallback((node: HTMLDivElement | null) => {
    if (loadingMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting && hasMore) {
        fetchMoreTransactions();
      }
    });
    if (node) observer.current.observe(node);
  }, [loadingMore, hasMore]);

  // Function to fetch more transactions
  const fetchMoreTransactions = async () => {
    if (!user || !hasMore || loadingMore) return;
    
    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const from = nextPage * TRANSACTIONS_PER_PAGE;
      
      const { data, error: fetchError } = await supabase
        .from('wallet_transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .range(from, from + TRANSACTIONS_PER_PAGE - 1);
        
      if (fetchError) throw fetchError;
      
      if (data && data.length > 0) {
        setTransactions(prev => [...prev, ...data]);
        setPage(nextPage);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      logger.error('Error fetching more transactions:', err);
      setError('Failed to load more transactions');
    } finally {
      setLoadingMore(false);
    }
  };

  // Check for payment result in URL (after redirect from payment provider)
  useEffect(() => {
    const checkPaymentResult = async () => {
      if (!user) return;
      
      // Parse URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const hasPaymentResult = urlParams.get('payment_result');
      const resourcePath = urlParams.get('resourcePath');

      if (hasPaymentResult && resourcePath) {
        try {
          setLoading(true);
          const result = await checkPeachPaymentStatus(resourcePath);
          
          if (result.success) {
            // Clear the URL parameters
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
            
            // Show success message
            setSuccessMessage('Payment processed successfully!');
            setError(null);
          } else {
            setError(result.error || 'Payment processing failed');
          }
        } catch (err) {
          logger.error('Error checking payment status:', err);
          setError(err instanceof Error ? err.message : 'Failed to verify payment status');
        } finally {
          setLoading(false);
        }
      }
    };

    checkPaymentResult();
  }, [user]);

  // Check FICA verification status
  useEffect(() => {
    if (!user) return;

    const checkFICAStatus = async () => {
      try {
        const { data, error: ficaError } = await supabase
          .from('fica_verification')
          .select('is_verified')
          .eq('user_id', user.id)
          .maybeSingle(); // Use maybeSingle to handle missing rows gracefully

        if (ficaError) {
          // If table doesn't exist (406, 42P01) or no row found, treat as not verified
          if (ficaError.code === 'PGRST116' || ficaError.code === '42P01' || ficaError.status === 406) {
            setFicaVerified(false);
            return;
          }
          logger.error('Error checking FICA status:', ficaError);
        } else {
          setFicaVerified(data?.is_verified ?? false);
        }
      } catch (err) {
        logger.error('Error checking FICA status:', err);
        setFicaVerified(false);
      }
    };

    checkFICAStatus();
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const fetchWalletData = async () => {
      try {
        setLoading(true);
        // Fetch wallet balance
        const { data: walletData, error: walletError } = await supabase
          .from('user_wallets')
          .select('balance')
          .eq('user_id', user.id)
          .single();

        if (walletError) {
          // If the wallet doesn't exist yet, create one with zero balance
          if (walletError.code === 'PGRST116') { // No rows returned error
            // Insert a new wallet with 0 balance
            const { data: newWallet, error: createError } = await supabase
              .from('user_wallets')
              .insert({ user_id: user.id, balance: 0 })
              .select()
              .single();
              
            if (createError) throw createError;
            
            if (newWallet) {
              setBalance(0); // New wallet has 0 balance
            }
          } else {
            throw walletError;
          }
        } else if (walletData) {
          setBalance((walletData as WalletData).balance);
        }

        // Reset pagination state
        setPage(0);
        setHasMore(true);

        // Fetch initial transactions batch
        const { data: transactionData, error: transactionError } = await supabase
          .from('wallet_transactions')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(TRANSACTIONS_PER_PAGE);

        if (transactionError) throw transactionError;
        setTransactions(transactionData || []);
        // Check if there are likely more transactions
        if (transactionData && transactionData.length < TRANSACTIONS_PER_PAGE) {
          setHasMore(false);
        }
      } catch (err) {
        logger.error('Error fetching wallet data:', err);
        setError('Failed to load wallet data');
      } finally {
        setLoading(false);
      }
    };

    fetchWalletData();

    // Subscribe to wallet changes
    const walletChannel = supabase.channel('wallet_realtime_changes');
    walletChannel
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_wallets',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          logger.info('Wallet change received!', payload);
          if ((payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') && payload.new && 'balance' in payload.new) {
             setBalance(payload.new.balance);
          } else if (payload.eventType === 'DELETE') {
            fetchWalletData();
          } else {
            fetchWalletData();
          }
        }
      )
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          logger.info('Subscribed to wallet changes!');
        }
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          logger.error('Wallet subscription error:', { status, err });
          setError('Realtime connection error for wallet.');
        }
      });

    // Subscribe to transaction changes
    const transactionChannel = supabase.channel('transaction_realtime_changes');
    transactionChannel
      .on(
        'postgres_changes',
        {
          event: 'INSERT', // Only care about new transactions
          schema: 'public',
          table: 'wallet_transactions',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          logger.info('New transaction received!', payload);
          const fetchTransactions = async () => {
            try {
              // Reset pagination state on new transactions
              setPage(0);
              setHasMore(true);
              
              const { data: transactionData, error: transactionError } = await supabase
                .from('wallet_transactions')
                .select('*')
                .eq('user_id', user.id)
                .order('created_at', { ascending: false })
                .limit(TRANSACTIONS_PER_PAGE);

              if (transactionError) throw transactionError;
              if (user) {
                setTransactions(transactionData || []);
                // Check if there are likely more transactions
                if (transactionData && transactionData.length < TRANSACTIONS_PER_PAGE) {
                  setHasMore(false);
                }
              }
            } catch (err) {
              logger.error('Error refetching transactions:', err);
            }
          };
          if (user) {
            fetchTransactions();
          }
        }
      )
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          logger.info('Subscribed to transaction changes!');
        }
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          logger.error('Transaction subscription error:', { status, err });
          setError('Realtime connection error for transactions.');
        }
      });

    return () => {
      supabase.removeChannel(walletChannel);
      supabase.removeChannel(transactionChannel);
    };
  }, [user]);

  const handleWithdraw = async (amount: number, reference: string = '') => {
    try {
      setLoading(true);
      
      // Create description with reference if provided
      const description = reference ? 
        `Manual withdrawal - Reference: ${reference}` : 
        'Manual withdrawal';
      
      // Use the existing process_withdrawal function
      const { error } = await supabase.rpc('process_withdrawal', {
        amount,
        description
      });

      if (error) {
        // Log the full error for debugging
        logger.error('Withdrawal error details:', error);
        // Close the modal first
        setShowWithdrawModal(false);
        // Then show the error message
        setError(error.message);
        return;
      }

      // Force refresh wallet data after successful withdrawal
      const { data: walletData, error: walletError } = await supabase
        .from('user_wallets')
        .select('balance')
        .eq('user_id', user?.id)
        .single();

      if (walletError) throw walletError;
      if (walletData) {
        setBalance((walletData as WalletData).balance);
        // Dispatch a custom event to notify other components
        window.dispatchEvent(new CustomEvent('walletBalanceUpdated', { 
          detail: { balance: (walletData as WalletData).balance } 
        }) as Event);
      }

      // Refresh transactions list
      const { data: transactionData, error: transactionError } = await supabase
        .from('wallet_transactions')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false })
        .limit(TRANSACTIONS_PER_PAGE);

      if (transactionError) throw transactionError;
      setTransactions(transactionData || []);
      
      // Set success message and close modal
      setSuccessMessage('Withdrawal request submitted successfully! An admin will process your request soon.');
      setShowWithdrawModal(false);
    } catch (err) {
      logger.error('Error processing withdrawal:', err);
      // Close the modal first
      setShowWithdrawModal(false);
      // Then show the error message
      setError(err instanceof Error ? err.message : 'Failed to process withdrawal');
    } finally {
      setLoading(false);
    }
  };

  // Calculate transaction statistics
  const getTransactionStats = () => {
    const totalDeposits = transactions
      .filter(tx => tx.type === 'deposit')
      .reduce((sum, tx) => sum + tx.amount, 0);
      
    const totalWithdrawals = transactions
      .filter(tx => tx.type === 'withdrawal')
      .reduce((sum, tx) => sum + tx.amount, 0);
  
    const totalBets = transactions
      .filter(tx => tx.type === 'bet_placement' || tx.type === 'service_fee')
      .reduce((sum, tx) => sum + tx.amount, 0);
      
    const totalWinnings = transactions
      .filter(tx => {
        // Include by transaction type
        if ((tx.type === 'bet_win' || tx.type === 'payout')) {
          return true;
        }
        // Also include by related entity type for completeness
        if (tx.related_entity_type === 'bet_payout') {
          return true;
        }
        return false;
      })
      .reduce((sum, tx) => sum + tx.amount, 0);

    const totalLosses = transactions
      .filter(tx => tx.type === 'bet_loss')
      .reduce((sum, tx) => sum + tx.amount, 0);

    const totalCancelledBets = transactions
      .filter(tx => (tx.type === 'bet_cancelled' || tx.type === 'fee_refund' || tx.type === 'market_cancellation_refund'))
      .reduce((sum, tx) => sum + Math.abs(tx.amount), 0);

    const totalAffiliateEarnings = transactions
      .filter(tx => tx.type === 'affiliate_commission')
      .reduce((sum, tx) => sum + tx.amount, 0);

    const totalCreatorEarnings = transactions
      .filter(tx => tx.type === 'creator_commission')
      .reduce((sum, tx) => sum + tx.amount, 0);

    return {
      totalDeposits,
      totalWithdrawals,
      totalBets,
      totalWinnings,
      totalLosses,
      totalCancelledBets,
      totalAffiliateEarnings,
      totalCreatorEarnings
    };
  };

  const stats = getTransactionStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 py-8">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-wayja-teal"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 pb-16">
      <div className="container mx-auto px-4 max-w-5xl">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Wallet Management</h1>

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg shadow-sm">
            {error}
            <button 
              className="ml-2 text-red-700 hover:text-red-900" 
              onClick={() => setError(null)}
            >
              <X className="inline w-4 h-4" />
            </button>
          </div>
        )}

        {ficaVerified === false && (
          <div className="mb-6 p-4 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded-lg shadow-sm">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium mb-1">FICA Verification Required</p>
                <p className="text-sm">
                  You need to submit and verify your FICA documents (ID copy, proof of address, and proof of bank details) before you can withdraw funds.
                </p>
                <button
                  onClick={() => {
                    setPendingAction('fica');
                    setShowVirtualCurrencyWarning(true);
                  }}
                  className="mt-2 text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
                >
                  Submit FICA Documents
                </button>
              </div>
            </div>
          </div>
        )}

        {successMessage && (
          <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg shadow-sm">
            {successMessage}
            <button 
              className="ml-2 text-green-700 hover:text-green-900" 
              onClick={() => setSuccessMessage(null)}
            >
              <X className="inline w-4 h-4" />
            </button>
          </div>
        )}

        {/* Balance & Stats Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Main Balance Card */}
          <div className="lg:col-span-1 bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col justify-between p-6 bg-gradient-to-br from-wayja-teal to-wayja-dark-teal text-white">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <Wallet className="w-7 h-7" />
                <h2 className="text-xl font-semibold">Available Balance</h2>
              </div>
              <p className="text-3xl font-bold tracking-tight mb-6">{formatCurrency(balance)}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  if (ficaVerified === false) {
                    setPendingAction('fica');
                    setShowVirtualCurrencyWarning(true);
                  } else {
                    setPendingAction('withdraw');
                    setShowVirtualCurrencyWarning(true);
                  }
                }}
                className="w-full px-4 py-3 bg-white/20 rounded-xl hover:bg-white/30 transition-all text-sm font-semibold flex items-center justify-center gap-2 backdrop-blur-sm"
              >
                <ArrowUpRight className="w-4 h-4" />
                Withdraw
              </button>
              {areDepositsDisabled(promoConfig) ? (
                <div className="relative group">
                  <button
                    type="button"
                    disabled
                    className="w-full px-4 py-3 bg-white/10 text-white/50 rounded-xl cursor-not-allowed transition-all text-sm font-semibold flex items-center justify-center gap-2"
                  >
                    <ArrowDownRight className="w-4 h-4" />
                    Deposit
                  </button>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                    {getDepositDisabledMessage(promoConfig)}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowDepositModal(true)}
                  className="w-full px-4 py-3 bg-white text-wayja-teal rounded-xl hover:bg-opacity-90 transition-all text-sm font-semibold flex items-center justify-center gap-2"
                >
                  <ArrowDownRight className="w-4 h-4" />
                  Deposit
                </button>
              )}
            </div>
          </div>
          
          {/* Stats Grid */}
          <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard 
              icon={DollarSign} 
              label="Total Deposits" 
              value={formatCurrency(stats.totalDeposits)} 
              color="green"
              to="/account/portfolio?tab=transactions"
            />
            <StatCard 
              icon={TrendingUp} 
              label="Total Winnings" 
              value={formatCurrency(stats.totalWinnings)} 
              color="green"
              to="/account/portfolio?tab=transactions"
            />
            <StatCard 
              icon={TrendingDown} 
              label="Total Withdrawals" 
              value={formatCurrency(stats.totalWithdrawals)} 
              color="red"
              to="/account/portfolio?tab=transactions"
            />
            <StatCard 
              icon={TrendingUp} 
              label="Total Trades" 
              value={formatCurrency(stats.totalBets)} 
              color="blue"
              to="/account/portfolio?tab=transactions"
            />
            <StatCard 
              icon={TrendingDown} 
              label="Total Losses" 
              value={formatCurrency(stats.totalLosses)} 
              color="red" 
            />
            <StatCard 
              icon={Repeat} 
              label="Cancelled Trades" 
              value={formatCurrency(stats.totalCancelledBets)} 
              color="gray" 
            />
            <StatCard 
              icon={Gift} 
              label="Affiliate Earnings" 
              value={formatCurrency(stats.totalAffiliateEarnings)} 
              color="purple" 
            />
            <StatCard 
              icon={Briefcase} 
              label="Creator Earnings" 
              value={formatCurrency(stats.totalCreatorEarnings)} 
              color="indigo" 
            />
          </div>
        </div>

        {/* Transactions History — full table on md+; mobile uses Portfolio link */}
        <div className="mb-6">
          <Link
            to="/account/portfolio?tab=transactions"
            className="md:hidden mb-4 flex w-full items-center justify-center rounded-xl bg-wayja-teal px-4 py-3.5 text-center text-sm font-semibold text-white shadow-md transition-colors hover:bg-wayja-dark-teal"
          >
            View Transactions
          </Link>

          {transactions.length === 0 ? (
            <div className="bg-white rounded-xl shadow-md overflow-hidden p-12 text-center">
              <div className="bg-gray-100 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4">
                <Inbox className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">No Transactions Yet</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                When you make {areDepositsDisabled(promoConfig) ? 'trades or withdrawals' : 'deposits, withdrawals, or place trades'}, your transactions will appear here.
              </p>
              {!areDepositsDisabled(promoConfig) && (
                <button
                  type="button"
                  onClick={() => setShowDepositModal(true)}
                  className="mt-6 px-5 py-2.5 bg-wayja-teal text-white rounded-lg hover:bg-wayja-teal/90 transition-colors text-sm font-medium"
                >
                  Make Your First Deposit
                </button>
              )}
            </div>
          ) : (
            <div className="hidden md:block bg-white rounded-xl shadow-md overflow-hidden">
              <TransactionHistory 
                transactions={transactions} 
                currentBalance={balance}
              />
              
              {hasMore && (
                <div 
                  ref={loadMoreTriggerRef}
                  className="py-6 text-center"
                >
                  {loadingMore ? (
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-wayja-teal"></div>
                      <span className="ml-2 text-gray-600">Loading more transactions...</span>
                    </div>
                  ) : (
                    <div className="text-gray-400 text-sm">Scroll down to load more transactions</div>
                  )}
                </div>
              )}
              
              {!hasMore && transactions.length > TRANSACTIONS_PER_PAGE && (
                <div className="py-4 text-center text-gray-500 text-sm">
                  All transactions loaded
                </div>
              )}
            </div>
          )}
        </div>

        {showDepositModal && (
          <DepositModal
            onClose={() => setShowDepositModal(false)}
            setSuccessMessage={setSuccessMessage}
          />
        )}

        {showWithdrawModal && (
          <WithdrawModal
            onClose={() => setShowWithdrawModal(false)}
            onWithdraw={(amount, reference) => handleWithdraw(amount, reference)}
            maxAmount={balance}
            formatCurrency={formatCurrency}
          />
        )}

        {showVirtualCurrencyWarning && (
          <VirtualCurrencyWarningModal
            onContinue={() => {
              setShowVirtualCurrencyWarning(false);
              if (pendingAction === 'fica') {
                setShowFICAModal(true);
              } else if (pendingAction === 'withdraw') {
                setShowWithdrawModal(true);
              }
              setPendingAction(null);
            }}
            onCancel={() => {
              setShowVirtualCurrencyWarning(false);
              setPendingAction(null);
            }}
          />
        )}

        {showFICAModal && (
          <FICADocumentModal
            onClose={() => setShowFICAModal(false)}
            onSuccess={async () => {
              // Refresh FICA status without closing the modal
              // User can continue uploading documents or close manually
              try {
                const { data, error } = await supabase
                  .from('fica_verification')
                  .select('is_verified')
                  .eq('user_id', user?.id)
                  .maybeSingle();
                
                if (error && error.code !== 'PGRST116' && error.code !== '42P01') {
                  logger.error('Error refreshing FICA status:', error);
                }
                
                setFicaVerified(data?.is_verified ?? false);
              } catch (err) {
                logger.error('Error refreshing FICA status:', err);
                setFicaVerified(false);
              }
            }}
          />
        )}
      </div>
    </div>
  );
}

// Reusable Stat Card Component
interface StatCardProps {
  icon: React.ElementType;
  label: string;
  value: string;
  color: 'green' | 'red' | 'blue' | 'purple' | 'indigo' | 'gray';
  /** When set, the card navigates to Portfolio (or another in-app route). */
  to?: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon: Icon, label, value, color, to }) => {
  const colorClasses = {
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
    blue: 'bg-blue-100 text-blue-700',
    purple: 'bg-purple-100 text-purple-700',
    indigo: 'bg-indigo-100 text-indigo-700',
    gray: 'bg-gray-100 text-gray-700',
  };

  const textClasses = {
    green: 'text-green-600',
    red: 'text-red-600',
    blue: 'text-blue-600',
    purple: 'text-purple-600',
    indigo: 'text-indigo-600',
    gray: 'text-gray-600',
  };

  const shellClass =
    'bg-white rounded-2xl shadow-md p-4 flex flex-col justify-between border border-gray-100 ' +
    (to ? 'hover:shadow-lg transition-shadow duration-300 cursor-pointer' : 'hover:shadow-lg transition-shadow duration-300');

  const body = (
    <>
      <div className="flex items-center space-x-3 mb-3">
        <div className={`p-2.5 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <h3 className="text-sm font-medium text-gray-600 flex-1 min-w-0">{label}</h3>
      </div>
      <p className={`text-base font-bold ${textClasses[color]} break-all`}>{value}</p>
    </>
  );

  if (to) {
    return (
      <Link to={to} className={`${shellClass} block text-inherit no-underline`}>
        {body}
      </Link>
    );
  }

  return <div className={shellClass}>{body}</div>;
};

interface DepositModalProps {
  onClose: () => void;
  setSuccessMessage: (message: string) => void;
}

function DepositModal({ onClose, setSuccessMessage }: DepositModalProps) {
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCardPayment, setShowCardPayment] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate input
    if (!amount) {
      setError('Please enter an amount');
      return;
    }
    
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Please enter a valid amount greater than 0');
      return;
    }
    
    setLoading(true);
    // The actual payment is now handled by PeachPaymentForm
    setShowCardPayment(true);
  };

  const handlePaymentSuccess = async () => {
    // Show success message or close modal
    setSuccessMessage('Your deposit was successful!');
    onClose();
  };

  const handlePaymentError = (errorMessage: string) => {
    setError(errorMessage);
    setLoading(false);
  };

  const handlePaymentCancel = () => {
    setShowCardPayment(false);
    setLoading(false);
  };

  const parsedAmount = parseFloat(amount) || 0;
  const paymentGatewayFee = parsedAmount * 0.04; // 4% fee
  const totalAmountToCharge = parsedAmount + paymentGatewayFee;

  // Show PeachPaymentForm when card payment is selected
  if (showCardPayment) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <PeachPaymentForm
          amount={totalAmountToCharge}
          walletCreditAmount={parsedAmount}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
          onCancel={handlePaymentCancel}
        />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-auto shadow-xl">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold flex items-center gap-2 text-gray-800">
            <div className="bg-wayja-teal text-white p-2 rounded-lg">
              <ArrowDownRight className="w-5 h-5" />
            </div>
            Deposit Funds
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Deposit Amount (ZAR)
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <span className="text-gray-500">R</span>
              </div>
              <input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-3 pl-8 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent text-lg"
                placeholder="0.00"
              />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Enter the amount you would like to add to your wallet
            </p>
          </div>

          {parsedAmount > 0 && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Payment Breakdown</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Deposit amount:</span>
                  <span className="font-medium">R{parsedAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment gateway fee (4%):</span>
                  <span className="font-medium">R{paymentGatewayFee.toFixed(2)}</span>
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-800">Total to be charged:</span>
                    <span className="font-bold text-gray-800">R{totalAmountToCharge.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              <p className="mt-3 text-xs text-gray-500">
                The fee covers payment processing costs. Your wallet will be credited with R{parsedAmount.toFixed(2)}.
              </p>
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Method
            </label>
            <div className="border rounded-lg p-4 border-wayja-teal bg-wayja-teal/5">
              <div className="flex flex-col items-center text-center">
                <CreditCard className="w-8 h-8 mb-2 text-wayja-teal" />
                <span className="font-medium">Card Payment</span>
                <span className="text-xs text-gray-500 mt-1">Pay with debit/credit card</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="mr-3 px-5 py-2.5 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 bg-wayja-teal text-white rounded-lg hover:bg-opacity-90 transition-colors flex items-center"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="mr-2">Processing...</span>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                </>
              ) : (
                'Continue to Payment'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface WithdrawModalProps {
  onClose: () => void;
  onWithdraw: (amount: number, reference: string) => Promise<void>;
  maxAmount: number;
  formatCurrency: (amount: number) => string;
}

function WithdrawModal({ onClose, onWithdraw, maxAmount, formatCurrency }: WithdrawModalProps) {
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate input
    if (!amount) {
      setError('Please enter an amount');
      return;
    }
    
    const withdrawAmount = parseFloat(amount);
    if (isNaN(withdrawAmount) || withdrawAmount <= 0) {
      setError('Please enter a valid amount greater than 0');
      return;
    }
    
    if (withdrawAmount > maxAmount) {
      setError(`Insufficient funds. Maximum withdrawal amount is ${formatCurrency(maxAmount)}`);
      return;
    }
    
    try {
      setLoading(true);
      await onWithdraw(withdrawAmount, reference);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process withdrawal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-auto shadow-xl">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold flex items-center gap-2 text-gray-800">
            <div className="bg-blue-500 text-white p-2 rounded-lg">
              <ArrowUpRight className="w-5 h-5" />
            </div>
            Withdraw Funds
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount (ZAR)
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <span className="text-gray-500">R</span>
              </div>
              <input
                type="number"
                min="0"
                max={maxAmount}
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-3 pl-8 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                placeholder="0.00"
              />
            </div>
            <div className="mt-2 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Available balance
              </p>
              <p className="text-sm font-medium">
                {formatCurrency(maxAmount)}
              </p>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bank Details / Reference
            </label>
            <textarea
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your bank account details or any additional payment information"
              rows={3}
            />
            <p className="mt-2 text-sm text-gray-500">
              Please provide your bank account details or any information needed to process your withdrawal
            </p>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="mr-3 px-5 py-2.5 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-opacity-90 transition-colors flex items-center"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="mr-2">Processing...</span>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                </>
              ) : (
                'Withdraw'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface VirtualCurrencyWarningModalProps {
  onContinue: () => void;
  onCancel: () => void;
}

function VirtualCurrencyWarningModal({ onContinue, onCancel }: VirtualCurrencyWarningModalProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-auto shadow-xl">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-800">Important Notice</h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-6">
          <div className="flex items-start gap-3 mb-4">
            <AlertCircle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
            <p className="text-gray-700 leading-relaxed">
              Thank you for submitting your FICA documents. At the moment, Wayja only operates using virtual currency that cannot be withdrawn from the system. If you would like to proceed and keep your FICA documentation on file, click Continue. If you would like to Cancel your FICA request until withdrawals are possible, click Cancel.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-2.5 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onContinue}
            className="px-5 py-2.5 bg-wayja-teal text-white rounded-lg hover:bg-opacity-90 transition-colors font-medium"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}